import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { ApplicationDemandDetail } from '../../../../../Interfaces/api/applications/demand-detail.interface';
import { GroupDemandDetail } from '../../../../../Interfaces/api/groups/demand-detail.interface';
import { DemandStatus } from '../../../../../Enums/demand-status.enum';
import { AuthService } from '../../../../Shared/Services/auth.service';
import { AvAvatarComponent } from '../../../../Shared/Components/av-avatar/av-avatar.component';
import { ActivityEvent } from '../../../Interfaces/demand-activity-log.interface';
import { SupportedDemand } from '../../../Types/supported-demand.type';

@Component({
  selector: 'app-demand-activity-log',
  standalone: true,
  imports: [COMMON_IMPORTS, AvAvatarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './demand-activity-log.component.html',
  styleUrl: './demand-activity-log.component.scss',
})
export class DemandActivityLogComponent {
  private readonly authService = inject(AuthService);

  readonly demand = input<SupportedDemand | null>(null);

  readonly events = computed<ActivityEvent[]>(() => {
    const d = this.demand();
    if (!d) return [];

    const currentUser = this.authService.getCurrentUser();
    const currentUserId = currentUser?.id ?? null;
    const selfName = currentUser?.name ?? '';
    const selfInitial = this.toInitial(currentUser?.name ?? null);
    const events: ActivityEvent[] = [];

    const creator = d.created_by;
    const createdBySelf = !!creator && !!currentUserId && creator.id === currentUserId;
    const createdActor = createdBySelf ? '' : this.formatActor(creator);
    const createdAvatar = createdBySelf
      ? selfName
      : createdActor.startsWith('app.')
        ? ''
        : createdActor;
    events.push({
      id: `created-${d.id}`,
      messageKey: createdBySelf
        ? 'app.catalog.myDemandDetail.activity.createdByYou'
        : 'app.catalog.myDemandDetail.activity.created',
      actorName: createdActor,
      onBehalfOf: null,
      step: null,
      initial: createdBySelf ? selfInitial : this.toInitial(createdActor),
      avatarName: createdAvatar,
      icon: 'add_circle',
      timestamp: d.created_at,
      ...this.relative(d.created_at),
    });

    for (const approval of d.approvals ?? []) {
      for (const history of approval.histories ?? []) {
        const statusName = history.status?.name ?? null;
        if (!statusName || statusName === DemandStatus.Ongoing) continue;
        const validator = history.validated_by;
        const onBehalfOfUser = history.on_behalf_of;
        const isCurrentUser =
          !onBehalfOfUser && !!validator && !!currentUserId && validator.id === currentUserId;
        const messageKey = this.messageKeyForStatus(statusName, isCurrentUser);
        if (!messageKey) continue;
        const onBehalfOf = onBehalfOfUser
          ? { delegate: this.formatActor(validator), delegator: this.formatActor(onBehalfOfUser) }
          : null;
        const actorName = onBehalfOf || isCurrentUser ? '' : this.formatActor(validator);
        const avatarSource = onBehalfOf ? onBehalfOf.delegate : actorName;
        const avatarName = isCurrentUser
          ? selfName
          : avatarSource.startsWith('app.')
            ? ''
            : avatarSource;
        events.push({
          id: history.id,
          messageKey,
          actorName,
          onBehalfOf,
          step: approval.step,
          initial: isCurrentUser ? selfInitial : this.toInitial(avatarSource),
          avatarName,
          icon: this.iconForStatus(statusName),
          timestamp: history.created_at,
          ...this.relative(history.created_at),
        });
      }
    }

    events.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    return events;
  });

  trackEvent(_index: number, event: ActivityEvent): string {
    return event.id;
  }

  private formatActor(
    validator: {
      first_name?: string;
      last_name?: string;
      email?: string;
      display_name?: string;
    } | null,
  ): string {
    if (!validator) return 'app.catalog.myDemandDetail.activity.unknown';
    if (validator.display_name?.trim()) return validator.display_name.trim();
    const parts = [validator.first_name, validator.last_name].filter(Boolean) as string[];
    const joined = parts.join(' ').trim();
    return joined || validator.email || 'app.catalog.myDemandDetail.activity.unknown';
  }

  private toInitial(name: string | null): string {
    if (!name) return '?';
    const trimmed = name.trim();
    if (!trimmed) return '?';
    return trimmed.charAt(0).toUpperCase();
  }

  private messageKeyForStatus(status: DemandStatus, isSelf: boolean): string | null {
    const base = 'app.catalog.myDemandDetail.activity.';
    switch (status) {
      case DemandStatus.Approved:
        return base + (isSelf ? 'approvedByYou' : 'approved');
      case DemandStatus.AutoApproved:
        return base + 'autoApproved';
      case DemandStatus.Rejected:
        return base + (isSelf ? 'rejectedByYou' : 'rejected');
      case DemandStatus.Canceled:
        return base + (isSelf ? 'canceledByYou' : 'canceled');
      case DemandStatus.ClosedCreatorRemoved:
        return base + 'closedCreatorRemoved';
      default:
        return null;
    }
  }

  private iconForStatus(status: DemandStatus): string | null {
    return status === DemandStatus.ClosedCreatorRemoved ? 'person_off' : null;
  }

  private relative(iso: string): {
    relativeKey: string;
    relativeValue: Record<string, number | string>;
    showAbsoluteDate: boolean;
  } {
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) {
      return {
        relativeKey: 'app.catalog.myDemandDetail.activity.relative.justNow',
        relativeValue: {},
        showAbsoluteDate: false,
      };
    }
    const diffMs = Date.now() - ts;
    const diffMin = Math.max(0, Math.floor(diffMs / 60_000));
    if (diffMin < 1) {
      return {
        relativeKey: 'app.catalog.myDemandDetail.activity.relative.justNow',
        relativeValue: {},
        showAbsoluteDate: false,
      };
    }
    if (diffMin < 60) {
      return {
        relativeKey: 'app.catalog.myDemandDetail.activity.relative.minutesAgo',
        relativeValue: { value: diffMin },
        showAbsoluteDate: false,
      };
    }
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) {
      return {
        relativeKey: 'app.catalog.myDemandDetail.activity.relative.hoursAgo',
        relativeValue: { value: diffHours },
        showAbsoluteDate: false,
      };
    }
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) {
      return {
        relativeKey: 'app.catalog.myDemandDetail.activity.relative.daysAgo',
        relativeValue: { value: diffDays },
        showAbsoluteDate: false,
      };
    }
    return {
      relativeKey: '',
      relativeValue: {},
      showAbsoluteDate: true,
    };
  }
}
